import { Injectable } from '@angular/core';

import { GeneratePdfDataService } from '../../../data/util/generate-pdf-data.service';
import { PdfService } from '../../../data/util/pdf.service';
import { USER_SUMMARY_I18N } from './user-summary.i18n';

export interface PdfOptionDefinition {
  label: string;
  attribute: string;
  generatorFactory: (
    service: GeneratePdfDataService,
  ) => (sectionId: string | null, userId: string, userDataDate: string | null) => Promise<PdfGeneratorResult>;
}

export interface PdfOption {
  label: string;
  attribute: string;
  checked: boolean;
  generator: (
    sectionId: string | null,
    userId: string,
    userDataDate: string | null,
  ) => Promise<PdfGeneratorResult>;
}

export interface PdfGeneratorResult {
  format: any[];
  data: Record<string, unknown>;
}

export interface GenerateUserSummaryPdfRequest {
  sectionDocuments: any[] | null;
  sectionId: string | null;
  userId: string;
  userDataDate: string | null;
  userDataAttributes: any[];
  pdfOptions: PdfOption[];
  userData: any | null;
  selectedUserWithUserData: any;
  chartImageDataUrl?: string | null;
}

@Injectable({ providedIn: 'root' })
export class UserSummaryPdfService {
  constructor(
    private readonly pdfService: PdfService,
    private readonly generatePdfDataService: GeneratePdfDataService,
  ) {}

  createPdfOptions(): PdfOption[] {
    return PDF_OPTION_DEFINITIONS.map(definition => ({
      label: definition.label,
      attribute: definition.attribute,
      checked: false,
      generator: definition.generatorFactory(this.generatePdfDataService),
    }));
  }

  async generate(request: GenerateUserSummaryPdfRequest): Promise<void> {
    const {
      sectionDocuments,
      sectionId,
      userId,
      userDataDate,
      userDataAttributes,
      pdfOptions,
      userData,
      selectedUserWithUserData,
      chartImageDataUrl,
    } = request;

    if (!sectionDocuments) {
      throw new Error('SECTION_DOCUMENTS_MISSING');
    }
    if (!sectionId) {
      throw new Error('SECTION_ID_MISSING');
    }
    if (!userData) {
      throw new Error('USER_DATA_MISSING');
    }

    const matchedDocument = sectionDocuments.find(doc => doc?.id === sectionId);
    if (!matchedDocument) {
      throw new Error('SECTION_DOCUMENT_NOT_FOUND');
    }

    const format = [...userDataAttributes];
    this.generatePdfDataService.restReportData();

    for (let i = pdfOptions.length - 1; i >= 0; i -= 1) {
      const option = pdfOptions[i];
      if (!option?.checked) {
        continue;
      }
      const pdfItem = await option.generator(sectionId, userId, userDataDate);
      if (Array.isArray(pdfItem.format)) {
        for (let j = pdfItem.format.length - 1; j >= 0; j -= 1) {
          const formattedSection = pdfItem.format[j];
          if (formattedSection && typeof formattedSection === 'object') {
            formattedSection.__fromPdfOption = true;
          }
          format.unshift(formattedSection);
        }
      }
      if (pdfItem.data) {
        Object.assign(userData.data, pdfItem.data);
      }
    }

    await this.pdfService.downloadPdf(
      selectedUserWithUserData,
      matchedDocument,
      format,
      userData,
      'summary',
      chartImageDataUrl ?? undefined,
    );
  }
}

export const PDF_OPTION_DEFINITIONS: PdfOptionDefinition[] = [
  {
    label: 'GOST',
    attribute: 'GOST',
    generatorFactory: service => service.generateGOSTReportData.bind(service),
  },
  {
    label: USER_SUMMARY_I18N.pdfOptions.gostDetail,
    attribute: 'GOST',
    generatorFactory: service => service.generateGOSTDetailReportData.bind(service),
  },
  {
    label: 'MWST',
    attribute: 'MWST',
    generatorFactory: service => service.generateMWSTReportData.bind(service),
  },
  {
    label: 'FOOD TEST',
    attribute: 'FOOD',
    generatorFactory: service => service.generateFOOD_TESTReportData.bind(service),
  },
  {
    label: 'RSST',
    attribute: 'RSST',
    generatorFactory: service => service.generateRSSTReportData.bind(service),
  },
  {
    label: 'SSTP',
    attribute: 'SSTPLUS',
    generatorFactory: service => service.generateSSTPReportData.bind(service),
  },
];
